import { Canvas } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Canvas/Canvas';
import { State } from './State';
import { StateValidator } from './StateValidator';
import { TriggerNode } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Canvas/Entities/Nodes/TriggerNode';
import { DisplayWebContentNode } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Canvas/Entities/Nodes/DisplayWebContentNode';
import { SendEmailNode } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Canvas/Entities/Nodes/SendEmailNode';
import { EndOfFlowNode } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Canvas/Entities/Nodes/EndOfFlowNode';
import { uuid } from 'utils';

export interface SingleFlowStateValidatorModel {
  showSegmentationSplit: boolean;
  onsiteMessageSegments?: {
    splitTestData?: {
      splitTestEnabled: boolean;
    };
    variants: {
      id: string;
    }[];
    offsiteMessageSegments?: {
      variants: {
        id: string;
      }[];
    }[];
  }[];
}

/**
 * The canvas is considered to be in a single flow state if:
 * - branching has not been enabled (showSegmentationSplit is false);
 * - There is a display website content added to the flow:
 *   - splitTestEnabled is true
 *   - onsiteMessageSegment's variants are populated
 * - There is a send email added to the flow:
 *   - offsiteMessageSegment's variants are populated
 */
export class SingleFlowStateValidator implements StateValidator {
  public constructor(private response: SingleFlowStateValidatorModel) {}

  public validate(): boolean {
    if (this.response.showSegmentationSplit) {
      return false;
    }

    const hasOffsiteMessage = !!this.response.onsiteMessageSegments
      ?.flatMap(s => s.offsiteMessageSegments?.flatMap(p => p.variants))
      .filter(s => s !== undefined).length;

    const hasOnsiteMessage =
      !!this.response.onsiteMessageSegments?.find(s => s.splitTestData?.splitTestEnabled) ||
      !!this.response.onsiteMessageSegments?.flatMap(s => s.variants).filter(s => s !== undefined).length;

    return hasOffsiteMessage || hasOnsiteMessage;
  }
}

export interface SingleFlowStateModel {
  onsiteMessageSegments?: {
    segmentId: string;
    splitTestData?: {
      splitTestEnabled: boolean;
    };
    variants: {
      id: string;
    }[];
    offsiteMessageSegments?: {
      variants: {
        id: string;
      }[];
    }[];
  }[];
}

/**
 * Creates a single flow for the canvas. The nodes added will depend on the onsite and offsite message that
 * has been saved.
 */
export class SingleFlowState implements State {
  public constructor(private validator: StateValidator, private model: SingleFlowStateModel) {}

  public createCanvas(): Canvas {
    if (!this.validator.validate()) {
      return;
    }

    const canvas = Canvas.createEmptyFlow(c => {
      const triggerRoot = new TriggerNode();

      this.model.onsiteMessageSegments?.forEach(onsiteMessage => {
        if (onsiteMessage.splitTestData?.splitTestEnabled) {
          triggerRoot.setNextNode(new DisplayWebContentNode(onsiteMessage.segmentId), false);
        }

        if (!onsiteMessage.splitTestData?.splitTestEnabled && onsiteMessage.variants?.length) {
          triggerRoot.setNextNode(new DisplayWebContentNode(onsiteMessage.variants[0].id), false);
        }

        onsiteMessage.offsiteMessageSegments?.forEach(offsiteMessage => {
          if (offsiteMessage.variants.length) {
            triggerRoot.setNextNode(new SendEmailNode(offsiteMessage.variants[0].id), false);
          }
        });
      });

      triggerRoot.setNextNode(new EndOfFlowNode(uuid()));

      return triggerRoot;
    });

    return canvas;
  }
}
